import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TSCard } from '@/components/shared/card/Card';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { workingPatternToEmployeeSchedule } from '@/helpers/workingPattern/employeeSchedule';
import { mergeWorkingPatternPeriods } from '@/helpers/workingPattern/workingPatternPeriods';
import { ScheduleEditForm } from '@/pages/team/teamMember/workRules/schedule/ScheduleEditForm';
import {
  ApprovalState,
  useEmployeeIdAppliedWorkingPatternsForPeriodQuery,
  useGetMeQuery,
  WorkingPatternPeriods
} from '@/services/gql/graphql.generated';
import { useSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { useMyPendingWorkPattern } from '@/services/workingPatterns/useMyPendingWorkPattern';
import { useScheduleRequestStatus } from '@/services/workingPatterns/useScheduleRequestStatus';
import { format } from 'date-fns';
import { FC, Suspense, useMemo } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { FiEdit } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import strings from '../../ess.translations';
import MyWorkingPatternRequestAlertCard from './MyWorkingPatternRequestUpdateCard';

interface MyScheduleEditProps {}

const MyScheduleEdit: FC<MyScheduleEditProps> = () => {
  const [{ data: userData, error: userError }] = useGetMeQuery();
  const handleError = useErrorHandler();
  const employeeId = userData?.myEmployee?.identityId;
  const navigate = useNavigate();
  const { pendingRequest, hasPendingRequest } = useScheduleRequestStatus(employeeId);
  const allowEssWorkingPatterns = useSystemSettings(state => state.settings.allowEssWorkingPatterns);

  const [{ data: scheduleData, error: scheduleError }, refetchSchedule] =
    useEmployeeIdAppliedWorkingPatternsForPeriodQuery({
      variables: {
        dateAndPeriods: {
          date: format(new Date(), 'yyyy-MM-dd'),
          periods: 7
        },
        id: employeeId!
      },
      requestPolicy: 'network-only',
      pause: !employeeId
    });

  if (userError || scheduleError) {
    handleError(userError || scheduleError);
  }

  if (!userData || !userData.myEmployee) {
    throw new Error('User data is required');
  }

  const breadcrumbs: NavPage[] = [
    { name: strings.mySchedule.header, to: `/ess/my-schedule` },
    { name: strings.mySchedule.editLabel, to: `/ess/my-schedule/edit` }
  ];

  const currentSchedule = scheduleData?.appliedWorkingPatternsByEmployeeId as WorkingPatternPeriods;

  const pendingSchedule = useMyPendingWorkPattern();

  const mergedSchedule = useMemo(
    () =>
      currentSchedule && pendingSchedule
        ? mergeWorkingPatternPeriods(currentSchedule, pendingSchedule)
        : currentSchedule,
    [currentSchedule, pendingSchedule]
  );

  const initialSchedule = useMemo(() => workingPatternToEmployeeSchedule(mergedSchedule), [mergedSchedule]);

  return (
    <PageContainer>
      <PageHeader icon={<FiEdit />} pageTitle={strings.mySchedule.editLabel} breadcrumbs={breadcrumbs} />
      <PageSizer mode={SizerMode.PAGE}>
        {!allowEssWorkingPatterns ? (
          <TSCard className="p-8 text-center">{strings.mySchedule.featureDisabled}</TSCard>
        ) : hasPendingRequest ? (
          <MyWorkingPatternRequestAlertCard
            status={ApprovalState.Requested}
            message="You have a pending schedule change request."
            onView={() => {
              if (pendingRequest?.id) {
                navigate(`/ess/my-schedule/change-request/${pendingRequest.id}`);
              }
            }}
          />
        ) : (
          <SubmissionErrorBoundary>
            <Suspense fallback={<TableLoader />}>
              <ScheduleEditForm
                userId={employeeId!}
                userFirstName={userData.myEmployee?.firstName}
                contractTypeId={userData.myEmployee?.contract?.contractTypeId}
                initSchedule={initialSchedule}
                refetchSchedule={refetchSchedule}
              />
            </Suspense>
          </SubmissionErrorBoundary>
        )}
      </PageSizer>
    </PageContainer>
  );
};

export default MyScheduleEdit;
